import { ValidatorFn, Validators } from '@angular/forms';
import { cloneDeep, get } from 'lodash-es';
import {
  BooleanOrUndefined,
  IFormValidationRules,
  isValidationRule,
  IValidationRules,
} from '../types';
import {
  isDateAfterMaxDate,
  isDateBeforeMinDate,
  MaxDate,
  MinDate,
} from './date-min-max-validation';
import { isDate } from './is-date';
import { Satisfies } from './satisfies-validation';

/**
Given a set of validation rules and a path into the ruleset, this function determines any Angular form validators that should be added
based on the rules at the path specified.
* @param path Comma separated path to navigate through object
* @param allRules The set of validation criteria which the object passed in the input field should meet.
* @param normalizedRules: Used when rule can't be found in allRules, perhaps due to the path being as an array, where a signle rule definition has been specified
* for all items in the array
* @returns an array of Angular form validators to apply
**/
export const getValidators = <T>(
  path: string[],
  allRules?: IFormValidationRules<T>,
  normalizedRules?: IFormValidationRules<T>
): ValidatorFn[] => {
  if (!path || path.length < 1 || !allRules) {
    return [];
  }
  let rules: IValidationRules = get(allRules, path);

  if (!rules) {
    rules = get(normalizedRules, path);
  }
  return getValidatorFunctions(rules);
};

export const getValidatorFunctions = (
  rules?: IValidationRules
): ValidatorFn[] => {
  const validators: ValidatorFn[] = [];
  if (rules?.required) {
    validators.push(Validators.required);
  }
  if (rules?.pattern) {
    validators.push(Validators.pattern(rules.pattern));
  }
  if (rules?.requiredTrue) {
    validators.push(Validators.requiredTrue);
  }
  if (rules?.maxDate) {
    validators.push(MaxDate(rules.maxDate));
  }
  if (rules?.minDate) {
    validators.push(MinDate(rules.minDate));
  }
  if (rules?.satisfies) {
    validators.push(Satisfies(rules.satisfies));
  }

  return validators;
};

export const valueMeetsRules = <T>(value?: T, rules?: IValidationRules) => {
  if (rules?.required) {
    if (value == undefined) {
      return false;
    } else if (typeof value === 'string' && !value) {
      return false;
    }
  }
  if (rules?.requiredTrue) {
    if (value == undefined) {
      return false;
    } else if (typeof value === 'boolean' && !value) {
      return false;
    }
  }
  if (rules?.pattern) {
    if (typeof value === 'string' && !rules.pattern.test(value)) {
      return false;
    }
  }
  if (rules?.maxDate) {
    if (isDate(value) && isDateAfterMaxDate(value, rules.maxDate)) {
      return false;
    }
  }
  if (rules?.minDate) {
    if (isDate(value) && isDateBeforeMinDate(value, rules.minDate)) {
      return false;
    }
  }
  if (rules?.satisfies) {
    if (rules.satisfies() !== null) {
      return false;
    }
  }
  return true;
};
/**
 * This function takes a list of rules, where the validation rules for an array have been specified as an object
 * to be applied to all entries, convert this to an array, with that item at index 0
 * This is to assist the code which finds validators based on path in the actual object.
 * @param allRules
 */
export const normalize = <T>(
  actual: T,
  rules?: IFormValidationRules<T>,
  path: string[] = []
): IFormValidationRules<T> | undefined => {
  if (Array.isArray(rules)) {
    return rules.reduce((agg, rule: IFormValidationRules<unknown>, index) => {
      const res = normalize(actual, rule, [...path, index.toString()]);
      if (res) {
        agg.push(res);
      }
      return agg;
    }, [] as IFormValidationRules<unknown[]>);
  } else if (typeof rules === 'object') {
    const objectInPath = get(actual, path) as IFormValidationRules<unknown[]>;
    const entries: [string, IFormValidationRules<unknown> | undefined][] = rules
      ? Object.entries(rules)
      : [];

    const newObj = entries.reduce(
      (
        agg: IFormValidationRules<unknown>,
        [key, rule]: [string, IFormValidationRules<unknown> | undefined],
        index
      ) => {
        const res = normalize(actual, rule, [...path, key]);
        if (res) {
          return {
            ...agg,
            [key]: res,
          };
        }
        return agg;
      },
      {} as IFormValidationRules<unknown>
    );

    // If path in actual object is to an array then force rule into array of actual object array size - this is the crux of the method
    if (Array.isArray(objectInPath)) {
      return objectInPath.map(
        () => newObj
      ) as unknown as IFormValidationRules<T>;
    }
    return newObj as IFormValidationRules<T>;
  }

  return cloneDeep(rules);
};

export const getKeysOfRulesPropertiesWithRulesSet = <T>(
  rules?: IFormValidationRules<Partial<T> | T> | IValidationRules
): string[] => {
  return rules
    ? Object.entries(rules)
        .filter(([key, value]) => {
          if (typeof value === 'object') {
            const result = Object.values(value).some(
              (subValue) => subValue !== undefined && subValue !== false
            );

            return result;
          }

          return value !== undefined && value !== false;
        })
        .map(([key]) => key)
    : [];
};

/**
Validate that all fields in input object T are valid based on provided rule definition
* @param input The object to validate
* @param rules The set of validation criteria which the object passed in the input field should meet.
* @returns a boolean indicating whether the input object is valid
*/
export const allFieldsAreValid = <T>(
  input: T,
  rules?: IFormValidationRules<Partial<T> | T> | IValidationRules
): [
  boolean,
  BooleanOrUndefined<T> | BooleanOrUndefined<T>[] | (boolean | undefined)
] => {
  if (Array.isArray(input)) {
    const rulesIsArray = (rules as IFormValidationRules<unknown[]>)?.length > 0;

    const validations: BooleanOrUndefined<T>[] = [];
    let valid = true;
    input.forEach((item, index) => {
      let ruleEntry:
        | IFormValidationRules<Partial<T> | T>
        | IValidationRules
        | undefined;
      if (rulesIsArray) {
        const rulesArray = rules as IFormValidationRules<unknown[]>;
        ruleEntry = rules ? rulesArray[index] : undefined;
      } else {
        ruleEntry = rules;
      }
      const [resp, validation] = allFieldsAreValid(item, ruleEntry);
      if (!resp) {
        valid = false;
      }
      validations.push(validation as BooleanOrUndefined<T>);
    });

    return [valid, validations as unknown as BooleanOrUndefined<T>];
  } else if (typeof input === 'object' && !isDate(input) && !isBlob(input)) {
    if (input === null) {
      return [!isValidationRule(rules), false];
    }

    const inputKeys = Object.keys(input);

    const validations: BooleanOrUndefined<T> = {} as BooleanOrUndefined<T>;
    let isValid = true;

    const rulesKeys = getKeysOfRulesPropertiesWithRulesSet(rules);
    rulesKeys.forEach((key) => {
      const propertyValue = inputKeys ? (input as any)[key] : undefined;
      const ruleEntry = rules ? (rules as any)[key] : undefined;

      const [resp, validationObj] = allFieldsAreValid(propertyValue, ruleEntry);
      (validations as any)[key] = validationObj;
      if (!resp) {
        isValid = false;
      }
    });

    return [isValid, validations];
  } else if (input === undefined && isValidationRule(rules)) {
    return [false, false];
  } else {
    const resp = valueMeetsRules(input, rules as IValidationRules);
    return [resp, resp];
  }
};

export const isBlob = (blob: unknown): blob is Blob => {
  const maybeBlob = blob as Blob;
  return maybeBlob?.size !== undefined && maybeBlob?.type !== undefined;
};
